import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { InfoNotice } from '@/components/patterns/info-notice'
import StripeConnectAlertsPanel from '@/components/stripe-connect/StripeConnectAlertsPanel'
import StripeConnectDashboardButton from '@/components/stripe-connect/StripeConnectDashboardButton'
import StripeMigrationInvitation from '@/components/stripe-connect/StripeMigrationInvitation'
import { hasDashboardlessStripeTools } from '@/components/stripe-connect/stripeConnectStatus'
import {
  STRIPE_CONNECT_ACCOUNT_SECTION,
  STRIPE_CONNECT_DASHBOARDLESS_SECTIONS,
  STRIPE_CONNECT_PAYOUT_SECTION,
} from '@/components/stripe-connect/stripeConnectSurfaceContent'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { StyledIcon } from '@/components/ui/styled-icon'
import { Card, CardDescription } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import {
  stripeAccountManagementLink,
  stripeCardReadersLink,
  stripeDisputesLink,
  stripePaymentsLink,
  stripePayoutsLink,
  stripePaymentMethodsLink,
} from '@/lib/stripe-financial-routes'
import { assertNotSalesDemoOrganization } from '@/lib/services/sales-demos/sales-demos.guard'
import {
  stripeConnectQueries,
  useStripeConnectStatus,
} from '@/lib/services/stripe-connect/stripe-connect.query'
import { createFileRoute, Link, notFound, redirect, useParams } from '@tanstack/react-router'
import {
  AlertCircleIcon,
  ArrowRight,
  CreditCard,
  HandCoins,
  Landmark,
  ShieldAlert,
  ShieldUser,
  ShoppingBag,
} from 'lucide-react'

function stripeCards(orgId, isDashboardless) {
  const cards = [
    {
      name: 'Payment methods',
      description: 'Choose whether to offer Klarna and Afterpay / Clearpay at storefront checkout.',
      icon: CreditCard,
      tone: 'amethyst',
      link: stripePaymentMethodsLink(orgId),
    },
    {
      name: STRIPE_CONNECT_ACCOUNT_SECTION.title,
      description: STRIPE_CONNECT_ACCOUNT_SECTION.description,
      icon: ShieldUser,
      tone: 'periwinkle',
      link: stripeAccountManagementLink(orgId),
    },
    {
      name: STRIPE_CONNECT_PAYOUT_SECTION.title,
      description: 'Review balances, payout timing, and recent Stripe payouts.',
      icon: HandCoins,
      tone: 'mint',
      link: stripePayoutsLink(orgId),
    },
  ]

  if (!isDashboardless) {
    return cards
  }

  return [
    ...cards,
    {
      ...STRIPE_CONNECT_DASHBOARDLESS_SECTIONS.payments,
      name: STRIPE_CONNECT_DASHBOARDLESS_SECTIONS.payments.title,
      icon: CreditCard,
      tone: 'amethyst',
      link: stripePaymentsLink(orgId),
    },
    {
      ...STRIPE_CONNECT_DASHBOARDLESS_SECTIONS.disputes,
      name: STRIPE_CONNECT_DASHBOARDLESS_SECTIONS.disputes.title,
      icon: ShieldAlert,
      tone: 'warning',
      link: stripeDisputesLink(orgId),
    },
    {
      ...STRIPE_CONNECT_DASHBOARDLESS_SECTIONS.cardReaders,
      name: STRIPE_CONNECT_DASHBOARDLESS_SECTIONS.cardReaders.title,
      icon: ShoppingBag,
      tone: 'periwinkle',
      link: stripeCardReadersLink(orgId),
    },
  ]
}

export const Route = createFileRoute('/_authenticated/$orgId/settings/stripe/')({
  component: StripeHubPage,
  loader: async ({ context, params }) => {
    const canEditStripeAccount = context.ability.can('edit', 'stripe-account')

    if (!canEditStripeAccount) {
      throw notFound()
    }

    await assertNotSalesDemoOrganization(context.queryClient, params.orgId)

    const stripeConnectStatus = await context.queryClient.fetchQuery(
      stripeConnectQueries.status({ organizationId: params.orgId, refresh: true })
    )
    const status = stripeConnectStatus?.status

    if (status === 'not_created' || status === 'incomplete') {
      throw redirect({
        to: '/$orgId/settings/stripe/onboarding',
        params: { orgId: params.orgId },
      })
    }
  },
})

function StripeHubContent() {
  const { orgId } = useParams({ strict: false })
  const { data: stripeConnectStatus } = useStripeConnectStatus({
    refresh: true,
    refetchOnMount: false,
  })
  const cards = stripeCards(orgId, hasDashboardlessStripeTools(stripeConnectStatus))

  return (
    <div className="space-y-6 pb-6">
      <AdminPageHeader
        icon={Landmark}
        title="Stripe"
        description="Manage Stripe setup, account details, and payouts."
        actions={<StripeConnectDashboardButton />}
      />

      <StripeMigrationInvitation organizationId={orgId} />
      <StripeConnectAlertsPanel showInitialPlaceholder noActionsNeededStateMode="resolved" />
      {stripeConnectStatus?.retained_account_disconnected ? (
        <InfoNotice tone="warning" title="Your payment account needs attention">
          Contact support to reconnect your existing account. Your previous payments and order
          history remain available.
        </InfoNotice>
      ) : null}
      <div className="space-y-4">
        {cards.map((card) => (
          <Link {...card.link} className="group block w-full" key={card.name}>
            <Card interactive size="flush">
              <div className="flex items-center justify-between gap-4 px-6 py-4">
                <div className="flex min-w-0 items-center gap-4">
                  <StyledIcon
                    icon={card.icon}
                    tone={card.tone}
                    className="flex h-10 w-10 shrink-0 items-center justify-center"
                    iconClassName="h-5 w-5"
                  />
                  <div className="min-w-0 space-y-1.5">
                    <Typography as="h2" variant="cardTitle">
                      {card.name}
                    </Typography>
                    <CardDescription>{card.description}</CardDescription>
                  </div>
                </div>

                <ArrowRight aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  )
}

export function StripeHubPage() {
  return (
    <IfGlobalOrg
      show={false}
      fallback={
        <Alert variant="info">
          <AlertCircleIcon />
          <AlertTitle>This is the global org Stripe page</AlertTitle>
          <AlertDescription>Do not set up a Stripe connect account.</AlertDescription>
        </Alert>
      }
    >
      <StripeHubContent />
    </IfGlobalOrg>
  )
}
